import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload, FileText, CheckCircle2, AlertTriangle, XCircle,
  History, Zap, Layers,
} from "lucide-react";
import { money, formatDateAT } from "@/lib/format";

const SOURCES = [
  { key: "auto", tr: "Otomatik", de: "Automatisch" },
  { key: "bank", tr: "Banka", de: "Bank" },
  { key: "wolt", tr: "Wolt", de: "Wolt" },
  { key: "foodora", tr: "Foodora", de: "Foodora" },
  { key: "lieferando", tr: "Lieferando", de: "Lieferando" },
  { key: "pos", tr: "POS", de: "POS" },
  { key: "supplier", tr: "Tedarikçi", de: "Lieferant" },
  { key: "accounting", tr: "Muhasebe", de: "Buchhaltung" },
  { key: "other", tr: "Diğer", de: "Sonstiges" },
];

const T = {
  tr: {
    title: "İçe Aktarma Merkezi",
    subtitle: "Banka, Wolt, Foodora, Lieferando, POS ve tedarikçi dosyalarını tek yerden yükle.",
    tabUpload: "Yükle", tabHistory: "Geçmiş", tabTemplates: "Şablonlar",
    step1: "Kaynak Türü", step2: "Dosya", step3: "Önizleme", step4: "Onayla",
    selectSource: "Otomatik algıla veya seç",
    dropOrPick: "Dosyayı sürükle veya seç (CSV / XLSX / PDF · max 10 MB)",
    pick: "Dosya Seç", parsing: "Dosya işleniyor…",
    detected: "Algılanan Kaynak", detectedFile: "Dosya",
    duplicateFile: "Bu dosya daha önce içe aktarıldı.",
    duplicateAt: "İçe aktarıldı", counts: "Satır Durumları",
    ready: "Hazır", needs: "İnceleme", dup: "Yinelenen", blocked: "Kapalı Dönem",
    mapping: "Kolon Eşleme",
    field: "Alan", column: "Kolon", empty: "—",
    rowsPreview: "Satır Önizleme (ilk 25)",
    date: "Tarih", amount: "Tutar", desc: "Açıklama", period: "Dönem", status: "Durum",
    confirm: "İçe Aktarmayı Onayla", skipDupBlock: "Yinelenen ve kapalı satırlar atlanacak.",
    reset: "Sıfırla",
    historyTitle: "İçe Aktarma Geçmişi", templatesTitle: "Eşleme Şablonları",
    noHistory: "Henüz içe aktarma yok.", noTemplates: "Şablon yok.",
    imported: "içe aktarıldı", failed: "başarısız",
    dupFile: "yinelenen", blockedFile: "engellendi",
    okConfirm: "İçe aktarma tamamlandı",
    errConfirm: "İçe aktarma başarısız",
    saveTemplate: "Şablonu kaydet",
    templateName: "Şablon adı",
  },
  de: {
    title: "Import-Center",
    subtitle: "Bank-, Wolt-, Foodora-, Lieferando-, POS- und Lieferantendateien zentral hochladen.",
    tabUpload: "Hochladen", tabHistory: "Verlauf", tabTemplates: "Vorlagen",
    step1: "Quelltyp", step2: "Datei", step3: "Vorschau", step4: "Bestätigen",
    selectSource: "Automatisch erkennen oder wählen",
    dropOrPick: "Datei hierher ziehen oder auswählen (CSV / XLSX / PDF · max 10 MB)",
    pick: "Datei wählen", parsing: "Datei wird verarbeitet…",
    detected: "Erkannte Quelle", detectedFile: "Datei",
    duplicateFile: "Diese Datei wurde bereits importiert.",
    duplicateAt: "Importiert am", counts: "Zeilenstatus",
    ready: "Bereit", needs: "Prüfen", dup: "Duplikat", blocked: "Geschlossene Periode",
    mapping: "Spaltenzuordnung",
    field: "Feld", column: "Spalte", empty: "—",
    rowsPreview: "Vorschau (erste 25 Zeilen)",
    date: "Datum", amount: "Betrag", desc: "Beschreibung", period: "Periode", status: "Status",
    confirm: "Import bestätigen", skipDupBlock: "Duplikate und geschlossene Zeilen werden übersprungen.",
    reset: "Zurücksetzen",
    historyTitle: "Importverlauf", templatesTitle: "Zuordnungsvorlagen",
    noHistory: "Noch keine Importe.", noTemplates: "Keine Vorlagen.",
    imported: "importiert", failed: "fehlgeschlagen",
    dupFile: "Duplikate", blockedFile: "blockiert",
    okConfirm: "Import erfolgreich",
    errConfirm: "Import fehlgeschlagen",
    saveTemplate: "Vorlage speichern",
    templateName: "Vorlagenname",
  },
};

const STATUS_BADGE = {
  ready: "bg-emerald-100 text-emerald-800 border-emerald-200",
  needs_review: "bg-amber-100 text-amber-800 border-amber-200",
  duplicate: "bg-slate-100 text-slate-700 border-slate-200",
  blocked_closed_period: "bg-rose-100 text-rose-800 border-rose-200",
  imported: "bg-sky-100 text-sky-800 border-sky-200",
  failed: "bg-rose-100 text-rose-800 border-rose-200",
  skipped: "bg-slate-100 text-slate-700 border-slate-200",
};

function StatusBadge({ status }) {
  const cls = STATUS_BADGE[status] || "";
  return <Badge className={`border ${cls}`} data-testid={`row-status-${status}`}>{status}</Badge>;
}

export default function ImportsPage() {
  const { lang } = useI18n();
  const t = T[lang] || T.tr;
  const [tab, setTab] = useState("upload");
  const [source, setSource] = useState("auto");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // upload response
  const [rows, setRows] = useState([]); // editable rows
  const [confirming, setConfirming] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [history, setHistory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const fileInput = useRef(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/imports");
      setHistory(data?.items || []);
    } catch { /* ignore */ }
  };
  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/imports/templates");
      setTemplates(data?.items || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (tab === "history") loadHistory();
    if (tab === "templates") loadTemplates();
  }, [tab]);

  const doUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Max 10 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = source && source !== "auto" ? `/imports/upload?source_type=${source}` : "/imports/upload";
      const { data } = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setRows(data.rows || []);
      toast.success(`${t.detected}: ${data.source_type}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setPreview(null); setRows([]); setSource("auto"); setTemplateName("");
    if (fileInput.current) fileInput.current.value = "";
  };

  const confirmImport = async () => {
    if (!preview || !rows.length) return;
    setConfirming(true);
    try {
      const payload = {
        source_type: preview.source_type,
        rows,
        mapping: preview.mapping,
        file_name: preview.file_name,
        file_hash: preview.file_hash,
        template_name: templateName || undefined,
      };
      const { data } = await api.post("/imports/confirm", payload);
      const j = data.job;
      toast.success(
        `${t.okConfirm} — ${j.imported_count} ${t.imported}` +
        (j.duplicate_count ? ` · ${j.duplicate_count} ${t.dupFile}` : "") +
        (j.blocked_count ? ` · ${j.blocked_count} ${t.blockedFile}` : "") +
        (j.failed_count ? ` · ${j.failed_count} ${t.failed}` : "")
      );
      reset();
      setTab("history");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.errConfirm);
    } finally {
      setConfirming(false);
    }
  };

  const setRowStatus = (idx, status) => {
    setRows(rs => rs.map((r, i) => (i === idx ? { ...r, status } : r)));
  };

  const counts = preview?.counts || {};
  const mapping = preview?.mapping || {};
  const canConfirm = rows.some(r => r.status === "ready");

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6" data-testid="imports-page">
      <PageHeader title={t.title} description={t.subtitle} />

      <Tabs value={tab} onValueChange={setTab} data-testid="imports-tabs">
        <TabsList className="rounded-full">
          <TabsTrigger value="upload" data-testid="imports-tab-upload"><Upload className="h-3.5 w-3.5 mr-1" />{t.tabUpload}</TabsTrigger>
          <TabsTrigger value="history" data-testid="imports-tab-history"><History className="h-3.5 w-3.5 mr-1" />{t.tabHistory}</TabsTrigger>
          <TabsTrigger value="templates" data-testid="imports-tab-templates"><Layers className="h-3.5 w-3.5 mr-1" />{t.tabTemplates}</TabsTrigger>
        </TabsList>

        {/* -------- UPLOAD -------- */}
        <TabsContent value="upload" className="space-y-4 mt-4">
          {!preview && (
            <Card data-testid="imports-upload-card">
              <CardHeader className="pb-2"><CardTitle className="text-base">{t.step1} · {t.step2}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-1">
                    <Label className="text-xs">{t.step1}</Label>
                    <Select value={source} onValueChange={setSource}>
                      <SelectTrigger data-testid="imports-source-select"><SelectValue placeholder={t.selectSource} /></SelectTrigger>
                      <SelectContent>
                        {SOURCES.map(s => (
                          <SelectItem key={s.key} value={s.key} data-testid={`imports-source-${s.key}`}>
                            {lang === "de" ? s.de : s.tr}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">{t.step2}</Label>
                    <div className="border-2 border-dashed rounded-xl p-6 text-center bg-muted/20" data-testid="imports-dropzone">
                      <FileText className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground mb-3">{t.dropOrPick}</p>
                      <input
                        ref={fileInput}
                        type="file"
                        accept=".csv,.xlsx,.xlsm,.pdf,.txt"
                        onChange={(e) => doUpload(e.target.files?.[0])}
                        className="hidden"
                        data-testid="imports-file-input"
                      />
                      <Button
                        size="sm" onClick={() => fileInput.current?.click()}
                        disabled={uploading} data-testid="imports-file-pick"
                      >
                        <Upload className="h-3.5 w-3.5 mr-1" />
                        {uploading ? t.parsing : t.pick}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {preview && (
            <>
              <Card data-testid="imports-preview-card">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-base">
                    {t.step3} · <span className="text-primary">{preview.source_type}</span>
                    <span className="text-xs text-muted-foreground ml-2">({preview.format?.toUpperCase()} · {preview.file_name})</span>
                  </CardTitle>
                  <Button size="sm" variant="ghost" onClick={reset} data-testid="imports-reset">{t.reset}</Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {preview.file_duplicate && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-center gap-2" data-testid="imports-file-duplicate">
                      <AlertTriangle className="h-4 w-4" />
                      <span>{t.duplicateFile} — {t.duplicateAt} {formatDateAT(preview.file_duplicate.created_at)}</span>
                    </div>
                  )}

                  {/* Counts */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="imports-counts">
                    <div className="rounded-lg border p-3 bg-emerald-50 border-emerald-200"><div className="text-[10px] uppercase tracking-wide text-emerald-800">{t.ready}</div><div className="text-lg font-semibold text-emerald-900" data-testid="cnt-ready">{counts.ready || 0}</div></div>
                    <div className="rounded-lg border p-3 bg-amber-50 border-amber-200"><div className="text-[10px] uppercase tracking-wide text-amber-800">{t.needs}</div><div className="text-lg font-semibold text-amber-900" data-testid="cnt-needs">{counts.needs_review || 0}</div></div>
                    <div className="rounded-lg border p-3 bg-slate-50 border-slate-200"><div className="text-[10px] uppercase tracking-wide text-slate-700">{t.dup}</div><div className="text-lg font-semibold text-slate-900" data-testid="cnt-dup">{counts.duplicate || 0}</div></div>
                    <div className="rounded-lg border p-3 bg-rose-50 border-rose-200"><div className="text-[10px] uppercase tracking-wide text-rose-800">{t.blocked}</div><div className="text-lg font-semibold text-rose-900" data-testid="cnt-blocked">{counts.blocked_closed_period || 0}</div></div>
                  </div>

                  {/* Mapping */}
                  <div data-testid="imports-mapping">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{t.mapping}</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      {Object.keys(mapping).length ? Object.entries(mapping).map(([k, v]) => (
                        <div key={k} className="rounded border p-2 bg-background">
                          <div className="text-muted-foreground uppercase">{k}</div>
                          <div className="font-medium truncate">{v || t.empty}</div>
                        </div>
                      )) : <div className="text-muted-foreground">{t.empty}</div>}
                    </div>
                  </div>

                  {/* Rows table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="imports-rows">
                      <thead className="bg-muted/40">
                        <tr>
                          <th className="text-left px-3 py-2 w-16">#</th>
                          <th className="text-left px-3 py-2">{t.date}</th>
                          <th className="text-right px-3 py-2">{t.amount}</th>
                          <th className="text-left px-3 py-2">{t.desc}</th>
                          <th className="text-left px-3 py-2">{t.period}</th>
                          <th className="text-left px-3 py-2">{t.status}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.slice(0, 25).map((r, i) => (
                          <tr key={i} className="border-t" data-testid={`imports-row-${i}`}>
                            <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                            <td className="px-3 py-2">{r.date || t.empty}</td>
                            <td className="px-3 py-2 text-right">{r.amount != null ? money(r.amount) : t.empty}</td>
                            <td className="px-3 py-2 truncate max-w-xs">{r.description || t.empty}</td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">{r.period_key || t.empty}</td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1">
                                <StatusBadge status={r.status} />
                                {r.status !== "skipped" && (
                                  <button
                                    className="text-xs text-muted-foreground hover:underline"
                                    onClick={() => setRowStatus(i, "skipped")}
                                    data-testid={`imports-row-skip-${i}`}
                                  >×</button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {rows.length > 25 && (
                      <div className="text-xs text-muted-foreground p-2">+ {rows.length - 25} more…</div>
                    )}
                  </div>

                  {/* Confirm bar */}
                  <div className="flex flex-col md:flex-row md:items-center gap-3 pt-3 border-t">
                    <div className="flex-1 flex items-center gap-2">
                      <Label className="text-xs">{t.templateName}</Label>
                      <Input
                        className="max-w-xs" placeholder="default"
                        value={templateName} onChange={(e) => setTemplateName(e.target.value)}
                        data-testid="imports-template-name"
                      />
                    </div>
                    <div className="text-xs text-muted-foreground flex-1">{t.skipDupBlock}</div>
                    <Button
                      onClick={confirmImport} disabled={confirming || !canConfirm}
                      data-testid="imports-confirm"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      {confirming ? "…" : t.confirm}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* -------- HISTORY -------- */}
        <TabsContent value="history" className="mt-4">
          <Card data-testid="imports-history-card">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t.historyTitle}</CardTitle></CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="text-sm text-muted-foreground" data-testid="imports-history-empty">{t.noHistory}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="imports-history-table">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="text-left px-3 py-2">{t.date}</th>
                        <th className="text-left px-3 py-2">{t.step1}</th>
                        <th className="text-left px-3 py-2">{t.detectedFile}</th>
                        <th className="text-right px-3 py-2">{t.ready}</th>
                        <th className="text-right px-3 py-2">{t.imported}</th>
                        <th className="text-right px-3 py-2">{t.dupFile}</th>
                        <th className="text-right px-3 py-2">{t.blockedFile}</th>
                        <th className="text-right px-3 py-2">{t.failed}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((j) => (
                        <tr key={j.id} className="border-t" data-testid={`imports-history-${j.id}`}>
                          <td className="px-3 py-2">{formatDateAT(j.created_at)}</td>
                          <td className="px-3 py-2">{j.source_type}</td>
                          <td className="px-3 py-2 truncate max-w-xs">{j.file_name}</td>
                          <td className="px-3 py-2 text-right">{j.row_count}</td>
                          <td className="px-3 py-2 text-right text-emerald-700">{j.imported_count}</td>
                          <td className="px-3 py-2 text-right text-slate-600">{j.duplicate_count}</td>
                          <td className="px-3 py-2 text-right text-rose-700">{j.blocked_count}</td>
                          <td className="px-3 py-2 text-right text-rose-700">{j.failed_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- TEMPLATES -------- */}
        <TabsContent value="templates" className="mt-4">
          <Card data-testid="imports-templates-card">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t.templatesTitle}</CardTitle></CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <div className="text-sm text-muted-foreground" data-testid="imports-templates-empty">{t.noTemplates}</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {templates.map(tp => (
                    <div key={`${tp.source_type}-${tp.name}`} className="rounded-lg border p-3" data-testid={`imports-template-${tp.source_type}-${tp.name}`}>
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">{tp.name} · <span className="text-primary">{tp.source_type}</span></div>
                        <div className="text-[10px] text-muted-foreground">{formatDateAT(tp.last_used_at)}</div>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                        {Object.entries(tp.column_mapping || {}).map(([k, v]) => (
                          <div key={k} className="truncate"><span className="text-muted-foreground">{k}: </span>{v}</div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
